import { useContext, useEffect, useState } from 'react';
import { Dialog } from '@mui/material';
import cloneDeep from 'lodash/cloneDeep';
// Our Components
import { AuthContext } from 'context/auth-context';
import { CARD_FORM_ERROR, CLOSE_REQUEST_CARD_FORM_MODAL, GET_CARDS } from 'context/cards/types';
import { CardContext } from 'context/cards/cardContext';
import CardIdentity from './steps/CardIdentity';
import CardOverview from './steps/CardOverview/CardOverview';
import CardType from './steps/CardType';
import CustomizeCard from './steps/CustomizeCard';
import OrderingCard from './steps/OrderingCard';
// Services
import { Cards } from 'lib/api';
// Utils and Helpers
import { REPLENISH_FRECUENCY } from 'helpers/constants';
import dayjs from 'helpers/dayjs';
// Assets
import './RequestCard.css';

export const CARD_FORM_STEP = {
    TYPE: 'TYPE',
    CARDHOLDER: 'CARDHOLDER',
    CUSTOMIZE: 'CUSTOMIZE',
    IDENTITY: 'IDENTITY',
    ORDERING: 'ORDERING',
    OVERVIEW: 'OVERVIEW',
    EDIT_USER: 'EDIT_USER'
};
const CARD_VALUES_TO_CREATE = {
    type: '',
    cardholderId: null,
    department: '',
    label: '',
    limitAmount: 0,
    memo: '',
    billingAddress: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        country: '',
        postalCode: ''
    },
    autoLock: '',
    has_manual_replenish: REPLENISH_FRECUENCY.ONE_TIME
};
const RequestCard = () => {
    const { dispatch, isRequestCardFormModalOpen, isFormSubmitting, request } = useContext(CardContext);
    const { userInfo } = useContext(AuthContext);
    const [cardForm, setCardForm] = useState(request);
    const [formStep, setFormStep] = useState(CARD_FORM_STEP.TYPE);
    const [requestedCard, setRequestedCard] = useState();

    useEffect(() => {
        setCardForm(request);
    }, [request]);

    const handleCardFormModalClose = (event, reason) => {
        if (isFormSubmitting) return;

        dispatch({ type: CLOSE_REQUEST_CARD_FORM_MODAL });
        setFormStep(CARD_FORM_STEP.TYPE);
        setCardForm(request);
    };

    const handleSubmit = async (values, actions) => {
        setCardForm({
            ...cardForm,
            ...values
        });
        setFormStep(CARD_FORM_STEP.ORDERING);
        try {
            await requestCard(values);
            setFormStep(CARD_FORM_STEP.OVERVIEW);
        } catch (error) {
            setFormStep(CARD_FORM_STEP.IDENTITY);
            dispatch({
                type: CARD_FORM_ERROR,
                payload: error?.response?.data?.message
            });
        }
    };

    const requestCard = async values => {
        const body = mapValuesToCreateCardModel(values);
        body.cardholderId = userInfo.id;
        const cardRequested = await Cards.requestCard(body);
        setRequestedCard(cardRequested);
        return dispatch({ type: GET_CARDS });
    };

    const mapValuesToCreateCardModel = values => {
        const cardValues = cloneDeep(values);
        const body = {};
        for (const key of Object.keys(cardValues)) {
            if (key in CARD_VALUES_TO_CREATE) {
                body[key] = cardValues[key];
                if (key === 'autoLock' && cardValues[key]) {
                    body[key] = dayjs(cardValues[key]).tz('America/Los_Angeles').startOf('day').toISOString();
                }
                if (key === 'has_manual_replenish') {
                    body[key] = cardValues[key] ? true : false;
                }
            }
        }
        return body;
    };

    const handleBackClick = currentStep => {
        setFormStep(currentStep);
    };

    const handleStepSubmit = (values, actions) => {
        setCardForm({
            ...cardForm,
            ...values
        });
        setFormStep(getNextStep());
    };

    const getNextStep = () => {
        if (formStep === CARD_FORM_STEP.TYPE) return CARD_FORM_STEP.CUSTOMIZE;
        if (formStep === CARD_FORM_STEP.CUSTOMIZE) return CARD_FORM_STEP.IDENTITY;
        if (formStep === CARD_FORM_STEP.IDENTITY) return CARD_FORM_STEP.ORDERING;
        if (formStep === CARD_FORM_STEP.ORDERING) return CARD_FORM_STEP.OVERVIEW;
    };

    const handleDoneClick = () => {
        setCardForm(request);
        setFormStep(CARD_FORM_STEP.TYPE);
        dispatch({ type: CLOSE_REQUEST_CARD_FORM_MODAL });
    };

    return (
        <>
            <Dialog open={isRequestCardFormModalOpen} onClose={() => {}} fullWidth maxWidth="md" sx={{ zIndex: 2 }}>
                {formStep === CARD_FORM_STEP.TYPE && (
                    <CardType
                        card={cardForm}
                        setCardForm={setCardForm}
                        onStepSubmit={handleStepSubmit}
                        onCardModalClose={handleCardFormModalClose}
                    />
                )}
                {formStep === CARD_FORM_STEP.CUSTOMIZE && (
                    <CustomizeCard
                        card={cardForm}
                        cardholder={userInfo}
                        onBackBtnClick={handleBackClick}
                        onStepSubmit={handleStepSubmit}
                        onCardModalClose={handleCardFormModalClose}
                    />
                )}
                {formStep === CARD_FORM_STEP.IDENTITY && (
                    <CardIdentity
                        card={cardForm}
                        cardholder={userInfo}
                        onBackBtnClick={handleBackClick}
                        onOrderCardClick={handleSubmit}
                        onCardModalClose={handleCardFormModalClose}
                    />
                )}
                {formStep === CARD_FORM_STEP.ORDERING && (
                    <OrderingCard card={cardForm} cardholder={userInfo} onCardModalClose={handleCardFormModalClose} />
                )}
                {formStep === CARD_FORM_STEP.OVERVIEW && (
                    <CardOverview
                        createdCard={requestedCard}
                        card={cardForm}
                        cardholder={userInfo}
                        onDoneClick={handleDoneClick}
                    />
                )}
            </Dialog>
        </>
    );
};

export default RequestCard;
